import { useApi } from '../api.js';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const fmt = (n) => `$${(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function Revenue() {
  const { data, error, loading, refresh } = useApi('/api/financials');
  if (loading) return <div className="loading">Loading financial data…</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const { summary = {}, monthly = [], arAging = [] } = data || {};

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Billing (QBO)</h2>
        <button className="refresh-btn" onClick={refresh}>Refresh</button>
      </div>

      <div className="cards">
        <div className="card"><div className="label">Revenue MTD</div><div className="value">{fmt(summary.revenueMTD)}</div></div>
        <div className="card"><div className="label">Revenue YTD</div><div className="value">{fmt(summary.revenueYTD)}</div></div>
        <div className="card"><div className="label">Outstanding A/R</div><div className="value">{fmt(summary.arTotal)}</div></div>
        <div className="card"><div className="label">Overdue A/R</div><div className="value" style={{color: summary.arOverdue > 0 ? '#dc2626' : 'inherit'}}>{fmt(summary.arOverdue)}</div></div>
      </div>

      <div className="chart-card">
        <h3>Monthly revenue (last 12 months)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthly}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis tickFormatter={(v) => `$${v / 1000}k`} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Line type="monotone" dataKey="revenue" stroke="#0ea5e9" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-card">
        <h3>A/R aging</h3>
        <table>
          <thead><tr><th>Customer</th><th>Current</th><th>1-30</th><th>31-60</th><th>61-90</th><th>90+</th><th>Total</th><th>Last payment</th><th>Invoices paid</th></tr></thead>
          <tbody>
            {arAging.map((r) => (
              <tr key={r.customer}>
                <td>{r.customer}</td>
                <td>{fmt(r.current)}</td>
                <td>{fmt(r.days1_30)}</td>
                <td style={{color: r.days31_60 > 0 ? '#c05100' : 'inherit'}}>{fmt(r.days31_60)}</td>
                <td style={{color: r.days61_90 > 0 ? '#dc2626' : 'inherit'}}>{fmt(r.days61_90)}</td>
                <td style={{color: r.days90plus > 0 ? '#dc2626' : 'inherit'}}>{fmt(r.days90plus)}</td>
                <td><strong>{fmt(r.total)}</strong></td>
                <td style={{whiteSpace: 'nowrap'}}>
                  {r.lastPayment
                    ? <>{r.lastPayment.date} · <strong>{fmt(r.lastPayment.amount)}</strong></>
                    : <span style={{color: '#94a3b8'}}>none on record</span>}
                </td>
                <td style={{fontSize: '0.85em', maxWidth: 260}}>
                  {r.lastPayment && r.lastPayment.invoicesPaid.length > 0
                    ? r.lastPayment.invoicesPaid.map(ip => ip.invoice + (ip.date ? ' (' + ip.date + ')' : '')).join(', ')
                    : '—'}
                </td>
              </tr>
            ))}
            {(() => {
              const tot = (k) => arAging.reduce((s2, r) => s2 + (Number(r[k]) || 0), 0);
              return (
                <tr style={{borderTop: '2px solid #94a3b8', background: '#f8fafc', fontWeight: 700}}>
                  <td>TOTAL ({arAging.length} customers)</td>
                  <td>{fmt(tot('current'))}</td>
                  <td>{fmt(tot('days1_30'))}</td>
                  <td style={{color: '#c05100'}}>{fmt(tot('days31_60'))}</td>
                  <td style={{color: '#dc2626'}}>{fmt(tot('days61_90'))}</td>
                  <td style={{color: '#dc2626'}}>{fmt(tot('days90plus'))}</td>
                  <td>{fmt(tot('total'))}</td>
                  <td colSpan={2} style={{fontWeight: 400, color: '#666', fontSize: '0.85em'}}>
                    overdue (31+): {fmt(tot('days31_60') + tot('days61_90') + tot('days90plus'))}
                  </td>
                </tr>
              );
            })()}
          </tbody>
        </table>
        <div className="note">Overdue = 31+ day buckets (1-30 is inside normal terms). Last payment pulled from QBO payments; invoice numbers resolved from the invoice cache.</div>
      </div>
    </>
  );
}
